import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { type DownloadLink } from "@/lib/movies-data";
import { getItemBySlug, useAllItems } from "@/lib/library-store";
import { getMovieBySlug as getContentMovie } from "@/lib/content/loader";
import { encryptUrl } from "@/lib/safelink-crypto";
import { blobDownload } from "@/lib/blob-download";
import { RawanPlayer } from "@/components/rawan/RawanPlayer";
import { useTheme } from "@/lib/theme-context";
import { Star, Clock, Calendar, Play, Download, Share2, Heart, Bookmark, Check, Eye, MessageSquare, Film, Flame, Sparkles } from "lucide-react";
import { PosterCard } from "@/components/rawan/PosterCard";

import { isFavorite, toggleFavorite, useFavorites } from "@/lib/favorites";
import { isOnWatchlist, toggleWatchlist, useWatchlist } from "@/lib/watchlist";
import { CommentsSection } from "@/components/rawan/CommentsSection";
import { AdSlot } from "@/components/rawan/AdSlot";
import { ArticleRenderer } from "@/components/rawan/ArticleRenderer";
import { ArticleSidebar } from "@/components/rawan/ArticleSidebar";
import { useEffect } from "react";
import { bumpViews, useViews, useCommentsCount } from "@/lib/firebase-data";
import { useState } from "react";
import { useIsHandheld } from "@/hooks/use-device";
import { matchesCountry, getCountryByCode } from "@/lib/countries";
import {
  OG_LOCALE, SUPPORTED_LANGS, UI_T, buildHreflangLinks,
  formatDate, pickGenres, pickOverview, pickTitle, withLang, pickDuration,
} from "@/lib/i18n";
import type { LanguageCode } from "@/lib/theme-context";
export const Route = createFileRoute("/movie/$slug")({
  validateSearch: (s: Record<string, unknown>) =>
    z.object({
      lang: z.enum(["ar", "fr", "en"]).optional(),
      country: z.string().optional(),
    }).parse(s),
  loaderDeps: ({ search }) => ({ lang: search.lang ?? "ar" }),
  loader: ({ params, deps }) => {
    // Static seed lookup; custom (localStorage) items resolve on the client.
    const movie = getItemBySlug(params.slug);
    return { movie: movie ?? null, slug: params.slug, lang: deps.lang as LanguageCode };
  },
  head: ({ loaderData }) => {
    const m = loaderData?.movie;
    if (!m) return {};
    const lang: LanguageCode = (loaderData?.lang as LanguageCode) || "ar";
    const c = m.country ? getCountryByCode(m.country) : undefined;
    const title = pickTitle(m, lang);
    const overview = pickOverview(m, lang);
    const t = UI_T[lang];
    const titleSuffix = c ? ` — ${t.movie} ${lang === "ar" ? c.ar : lang === "fr" ? c.fr : c.en}` : "";
    const basePath = `/movie/${m.slug}`;
    const canonical = withLang(basePath, lang);
    return {
      meta: [
        { title: `${title} (${m.year})${titleSuffix} — Rawan UI` },
        { name: "description", content: overview.slice(0, 155) },
        { name: "keywords", content: [...pickGenres(m, lang), m.titleEn, title].join(", ") },
        // Open Graph (localized)
        { property: "og:title", content: `${title} (${m.year})${titleSuffix}` },
        { property: "og:description", content: overview.slice(0, 200) },
        { property: "og:image", content: m.poster },
        { property: "og:image:secure_url", content: m.poster },
        { property: "og:image:alt", content: `${title} — ${t.movie}` },
        { property: "og:image:width", content: "500" },
        { property: "og:image:height", content: "750" },
        { property: "og:type", content: "video.movie" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: OG_LOCALE[lang] },
        ...SUPPORTED_LANGS.filter((l) => l !== lang).map((l) => ({
          property: "og:locale:alternate", content: OG_LOCALE[l],
        })),
        // Twitter
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${title} (${m.year})${titleSuffix}` },
        { name: "twitter:description", content: overview.slice(0, 200) },
        { name: "twitter:image", content: m.poster },
        { name: "twitter:image:alt", content: title },
      ],
      links: [
        { rel: "canonical", href: canonical },
        ...buildHreflangLinks(basePath),
      ],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Movie",
          name: title,
          alternateName: [m.title, m.titleEn, m.titleFr].filter(Boolean),
          image: m.poster,
          description: overview,
          datePublished: m.releaseDate || String(m.year),
          inLanguage: lang,
          genre: pickGenres(m, lang),
          duration: m.duration,
          ...(c ? { countryOfOrigin: { "@type": "Country", name: c.en } } : {}),
          aggregateRating: { "@type": "AggregateRating", ratingValue: m.rating, bestRating: 10, ratingCount: 1000 },
          actor: m.cast.map((n) => ({ "@type": "Person", name: n })),
          director: { "@type": "Person", name: m.director },
        }),
      }],
    };
  },
  component: MoviePage,
  notFoundComponent: () => (
    <div className="min-h-dvh grid place-items-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">الفيلم غير موجود / Movie not found / Film introuvable</h1>
        <Link to="/" className="btn-primary inline-flex">العودة للرئيسية / Home / Accueil</Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => {
    if (typeof window !== "undefined") console.error("[movie route]", error);
    return (
      <div className="min-h-dvh flex flex-col">
        <main className="flex-1 grid place-items-center px-4 py-16">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-extrabold mb-2">تعذّر تحميل صفحة الفيلم / Could not load movie page / Impossible de charger la page du film</h1>
            <p className="text-sm text-muted-foreground mb-6">جرّب التحديث، أو عد للرئيسية وحاول مجدداً. / Try refreshing or return home and try again. / Essayez d’actualiser ou revenez à l’accueil.</p>
            <div className="flex justify-center gap-2 flex-wrap">
              <button onClick={() => window.location.reload()} className="btn-primary inline-flex">إعادة المحاولة / Retry / Réessayer</button>
              <Link to="/" className="px-5 py-2.5 rounded-lg border border-border hover:bg-accent">الرئيسية / Home / Accueil</Link>
            </div>
          </div>
        </main>
      </div>
    );
  },
});
function MoviePage() {
  const { movie: initial, slug, lang: routeLang } = Route.useLoaderData();
  const search = Route.useSearch();
  const allItems = useAllItems();
  const movie = initial ?? allItems.find((m) => m.slug === slug);
  const { language } = useTheme();
  const navigate = useNavigate();
  const [trailerOpen, setTrailerOpen] = useState(false);
  const isHandheld = useIsHandheld();
  // Search param wins, then route loader lang, then user theme language.
  const lang: LanguageCode = (search.lang as LanguageCode) ?? routeLang ?? language ?? "ar";
  const UI = UI_T[lang];
  const t = (ar: string, en: string, fr?: string) => (lang === "ar" ? ar : lang === "fr" ? (fr ?? en) : en);

  // Scroll to #watch or #article anchor on mount (e.g. from Hero CTAs)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace("#", "");
    if (hash !== "watch" && hash !== "article") return;
    const tries = [80, 250, 600, 1200];
    const timers = tries.map((ms) =>
      window.setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, ms),
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, []);

  async function openSafe(url: string) {
    try {
      const token = await encryptUrl(url);
      navigate({ to: "/safelink", search: { t: token, d: 1 } as any });
    } catch {
      navigate({ to: "/safelink", search: { url } as any });
    }
  }
  /** يفتح مشغّل RawanUI في صفحة /play مع رابط محمي بالطبقتين.
   *  يفضّل استخدام watch_token المُعدّ مسبقاً (من ملفات JSON) لتجنّب
   *  تكرار التشفير عند كل نقرة. */
  async function openPlay(url: string, quality?: string) {
    // ملاحظة مهمة: كل جودة قد تحمل رابطاً مختلفاً — لذا لا نستخدم
    // movie.watchToken (الخاص بالرابط الرئيسي) إلا حين يطابق URL الجودة
    // المضغوطة الرابط الرئيسي. لأي جودة أخرى نُشفّر رابطها الخاص لضمان
    // أن يُشغَّل مصدر تلك الجودة تحديداً.
    const raw = (movie as unknown as { __raw?: { watch_url?: string } })?.__raw;
    const canonicalWatchUrl = raw?.watch_url;
    let token: string | undefined;
    if (movie?.watchToken && canonicalWatchUrl && url === canonicalWatchUrl) {
      token = movie.watchToken;
    } else {
      const { encryptDouble } = await import("@/lib/safelink-crypto");
      token = await encryptDouble(url);
    }
    const { allowNextInternalNav } = await import("@/lib/internal-nav");
    allowNextInternalNav();
    try {
      const { recordWatch } = await import("@/lib/watch-history");
      recordWatch({ slug: movie!.slug, kind: "movie" });
    } catch { /* ignore */ }
    navigate({ to: "/play", search: { t: token, slug: movie!.slug, kind: "movie", q: quality } as any });
  }

  useEffect(() => {
    if (movie?.slug) bumpViews(movie.slug);
  }, [movie?.slug]);
  // Sync ?lang= with global header language so share URLs/OG reflect current locale.
  useEffect(() => {
    if (language && search.lang !== language) {
      navigate({ search: { ...(search as any), lang: language } as any, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);
  if (!movie) {
    return (
      <div className="min-h-dvh flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-extrabold">{t("الفيلم غير موجود", "Movie not found", "Film introuvable")}</h1>
          <Link to="/" className="btn-primary inline-flex mt-6">{t("\u0627\u0644\u0631\u0626\u064a\u0633\u064a\u0629", "Home", "Accueil")}</Link>
        </main>
        <Footer />
      </div>
    );
  }
  const related = allItems.filter((x) => x.type === movie.type && x.id !== movie.id).slice(0, 6);
  const sameCountry = movie.country
    ? allItems
        .filter((x) => x.type === movie.type && x.id !== movie.id && matchesCountry(x.country, movie.country!))
        .slice(0, 6)
    : [];
  const countryEntry = movie.country ? getCountryByCode(movie.country) : undefined;
  const article = (movie as { article?: import("@/lib/article-types").Article }).article ?? [];
  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero backdrop */}
        <section className="relative">
          <div className="absolute inset-0 overflow-hidden">
            <img src={movie.backdrop} alt="" aria-hidden className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/80 to-black/70" />
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent" />
          </div>
          <div className="relative container mx-auto px-4 pt-28 pb-8">
            <div className="grid md:grid-cols-[240px_1fr] gap-6 items-start md:items-stretch">
              {/* Poster */}
              <div className="poster-card animate-scale-in shadow-poster max-w-[240px] mx-auto md:mx-0 md:max-w-none md:h-full md:self-stretch">
                <img src={movie.poster} alt={pickTitle(movie, lang)} className="w-full aspect-[2/3] md:aspect-auto md:h-full object-cover" />
              </div>

              {/* Details */}
              <div className="relative animate-fade-in rounded-2xl border border-white/15 bg-black/55 p-4 md:p-6 text-white shadow-2xl backdrop-blur-md">
                <BadgeBar
                  kind="movie"
                  slug={movie.slug}
                  threadId={`movie-${movie.slug}`}
                  isNew={!!movie.isNew}
                  isTrending={!!movie.isTrending}
                  lang={lang}
                />
                <h1 className="text-3xl md:text-5xl font-extrabold leading-tight text-white drop-shadow-lg mt-3">
                  {pickTitle(movie, lang)}
                </h1>
                <p className="text-lg text-white/80 mt-1">{movie.titleEn}</p>
                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-white/90">
                  <span className="flex items-center gap-1.5 font-bold">
                    <Star className="h-4 w-4 fill-[var(--rating)] text-[var(--rating)]" />
                    {movie.rating}/10
                  </span>
                  <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {movie.releaseDate ? formatDate(movie.releaseDate, lang) : movie.year}</span>
                  <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {pickDuration(movie, lang)}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {pickGenres(movie, lang).map((g: string) => (
                    <span key={g} className="text-xs px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white backdrop-blur">{g}</span>
                  ))}
                </div>
                <p className="mt-5 text-base leading-relaxed text-white/85 max-w-3xl">
                  {pickOverview(movie, lang)}
                </p>
                
                {/* Meta — light weight */}
                <dl className="mt-5 grid sm:grid-cols-2 gap-3 text-sm font-light">
                  {movie.releaseDate && (
                    <div className="flex gap-2"><dt className="text-white/60 min-w-20">{UI.releaseDate}:</dt><dd className="text-white/90">{formatDate(movie.releaseDate, lang)}</dd></div>
                  )}
                  <div className="flex gap-2"><dt className="text-white/60 min-w-20">{UI.director}:</dt><dd className="text-white/90">{movie.director}</dd></div>
                  <div className="flex gap-2"><dt className="text-white/60 min-w-20">{UI.cast}:</dt><dd className="text-white/90">{movie.cast.join("، ")}</dd></div>
                </dl>
                {/* Actions — responsive sizing to avoid wrap on mobile */}
                <MovieActions movie={movie} t={t} lang={lang} onTrailer={() => setTrailerOpen((v) => !v)} trailerOpen={trailerOpen} />
              </div>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4"><AdSlot slot="movie_top" format="horizontal" /></div>

        {/* مشغّل التريلر — يُحقن داخل صفحة الفيلم فقط عند الضغط على زر "التريلر" */}
        {trailerOpen && movie.trailer && (
          <section className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl sm:text-2xl font-extrabold flex items-center gap-2">
                <Play className="h-5 w-5 text-primary fill-current" />
                 {t("مشاهدة التريلر", "Watch trailer", "Voir la bande-annonce")}
              </h2>
              <button onClick={() => setTrailerOpen(false)} className="text-sm text-muted-foreground hover:text-primary">
                {t("\u0625\u063a\u0644\u0627\u0642", "Close", "Fermer")}
              </button>
            </div>
            <RawanPlayer url={movie.trailer} poster={movie.backdrop} title={pickTitle(movie, lang)} ads={false} />
            <AdSlot slot="movie_below_player" format="horizontal" />
          </section>
        )}

        {/* Download section */}
        {/* Download/Watch buttons — hidden on real desktops (mouse + no touch),
            kept available on phones & tablets even if window is wide
            (landscape, "request desktop site", browser zoom-out, etc.).
            JS guard is the single source of truth — no CSS width gates here. */}
        {isHandheld && (
        <section id="watch" style={{ scrollMarginTop: "96px" }} className="container mx-auto px-4 py-10">
          <div className="bg-card rounded-2xl border border-border p-6 md:p-8 shadow-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="grid place-items-center w-10 h-10 rounded-xl" style={{ background: "var(--gradient-primary)" }}>
                <Download className="h-5 w-5 text-primary-foreground" />
              </div>
              <div>
                <h2 className="text-2xl font-extrabold">{t("\u0631\u0648\u0627\u0628\u0637 \u0627\u0644\u062a\u062d\u0645\u064a\u0644 \u0648\u0627\u0644\u0645\u0634\u0627\u0647\u062f\u0629", "Download & Watch", "T\u00e9l\u00e9chargements & Streaming")}</h2>
                <p className="text-sm text-muted-foreground">{t("\u0627\u062e\u062a\u0631 \u0627\u0644\u062c\u0648\u062f\u0629 \u0627\u0644\u0645\u0646\u0627\u0633\u0628\u0629 \u0644\u0627\u062a\u0635\u0627\u0644\u0643", "Pick the quality that fits your connection", "Choisissez la qualit\u00e9 adapt\u00e9e \u00e0 votre connexion")}</p>
              </div>
            </div>
            <div className="grid gap-3">
               {movie.downloads.map((d: DownloadLink, i: number) => (
                <div
                  key={i}
                  className="group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 p-3 rounded-xl bg-background border border-border hover:border-primary transition-all hover:shadow-glow"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="shrink-0 text-xs font-extrabold px-2.5 py-1.5 rounded-lg text-primary-foreground" style={{ background: "var(--gradient-primary)" }}>
                      {d.quality}
                    </span>
                    <div className="min-w-0">
                      <div className="font-bold truncate">{d.server}</div>
                      <div className="text-xs text-muted-foreground truncate">{t("الحجم", "Size", "Taille")}: {d.size}</div>
                    </div>
                  </div>
                   <div className="flex gap-2 shrink-0 justify-self-end">
                    <button type="button" onClick={() => openPlay(d.url, d.quality)} aria-label={t("\u0645\u0634\u0627\u0647\u062f\u0629", "Watch", "Regarder")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary/10 hover:bg-primary hover:text-primary-foreground text-primary border border-primary/30 transition text-xs font-semibold whitespace-nowrap">
                      <Play className="h-3.5 w-3.5 fill-current" /> <span>{t("\u0645\u0634\u0627\u0647\u062f\u0629", "Watch", "Regarder")}</span>
                    </button>
                    <button type="button" onClick={() => navigate({ to: "/download/movie/$slug", params: { slug: movie.slug } })} aria-label={t("\u062a\u062d\u0645\u064a\u0644", "Download", "T\u00e9l\u00e9charger")} className="inline-flex items-center gap-1.5 btn-primary !py-2 !px-2.5 text-xs whitespace-nowrap">
                      <Download className="h-3.5 w-3.5" /> <span>{t("\u062a\u062d\u0645\u064a\u0644", "Download", "T\u00e9l\u00e9charger")}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4 text-center">
              {t("روابط التحميل تمر عبر صفحة حماية ومشغّل RawanUI المشفّر.", "Links pass through the safelink page and RawanUI encrypted player.", "Les liens passent par la page de protection et le lecteur chiffré RawanUI.")}
            </p>
          </div>
        </section>
        )}

        {/* Rich article — with sticky social + comments sidebar on desktop */}
        <ArticleRenderer
          article={article}
          lang={lang}
          rightColumn={
            isHandheld ? undefined : (
            <ArticleSidebar
              lang={lang}
              threadId={`movie-${movie.slug}`}
              title={`${pickTitle(movie, lang)} (${movie.year})`}
            />
            )
          }
        />

        {/* Comments below the article on mobile/tablet */}
        {isHandheld && (
          <div>
            <CommentsSection
            threadId={`movie-${movie.slug}`}
            title={`${pickTitle(movie, lang)} (${movie.year})`}
          />
          </div>
        )}

        {/* Related */}
        <section className="container mx-auto px-4 py-10">
          <h2 className="text-2xl font-extrabold mb-5">{t("\u0642\u062f \u064a\u0639\u062c\u0628\u0643 \u0623\u064a\u0636\u0627\u064b", "You may also like", "\u00c0 voir aussi")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {related.map((m) => <PosterCard key={m.id} movie={m} />)}
          </div>
        </section>
        {/* Same-country recommendations */}
        {sameCountry.length > 0 && countryEntry && (
          <section className="container mx-auto px-4 pb-12">
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <h2 className="text-2xl font-extrabold">
                <span aria-hidden className="me-2">{countryEntry.flag}</span>
                {lang === "ar" ? `أفلام ${countryEntry.ar} أخرى` : lang === "fr" ? `Autres films ${countryEntry.fr}` : `More ${countryEntry.en} Movies`}
              </h2>
              <Link
                to="/movies"
                search={{ country: countryEntry.code }}
                className="text-sm font-semibold text-primary hover:underline"
              >
                {t("عرض الكل", "View all", "Tout voir")} →
              </Link>
            </div>
            {sameCountry.length === 0 ? (
              <div className="text-muted-foreground text-sm">{t("لا توجد عناوين أخرى من نفس الدولة بعد.", "No other titles from this country yet.", "Aucun autre titre de ce pays pour le moment.")}</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                {sameCountry.map((m) => <PosterCard key={m.id} movie={m} />)}
              </div>
            )}
          </section>
        )}

      </main>
      <Footer />
    </div>
  );
}




/* ---------- MovieActions: favorite / watchlist / share / trailer ---------- */
function MovieActions({
  movie,
  t,
  lang,
  onTrailer,
  trailerOpen,
}: {
  movie: { slug: string; type: "movie" | "series"; title: string; titleEn: string; titleFr?: string; trailer: string };
  t: (ar: string, en: string, fr?: string) => string;
  lang: LanguageCode;
  onTrailer?: () => void;
  trailerOpen?: boolean;
}) {
  useFavorites(); // subscribe
  useWatchlist();
  const fav = isFavorite(movie.slug);
  const saved = isOnWatchlist(movie.slug);
  const [shared, setShared] = useState(false);

  async function share() {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}/movie/${movie.slug}?lang=${lang}`;
    const shareTitle = lang === "ar" ? movie.title : lang === "fr" ? (movie.titleFr || movie.titleEn) : movie.titleEn;
    try {
      if (typeof navigator !== "undefined" && (navigator as Navigator).share) {
        await (navigator as Navigator).share({ title: shareTitle, text: shareTitle, url });
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 1500);
      }
    } catch { /* user cancelled — silent */ }
  }

  const baseBtn =
    "inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold border transition whitespace-nowrap";

  return (
    <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-6">
      <button
        type="button"
        onClick={onTrailer}
        aria-pressed={!!trailerOpen}
        className="btn-primary inline-flex items-center gap-1.5 sm:gap-2 !px-3 sm:!px-5 !py-1.5 sm:!py-2.5 text-xs sm:text-sm"
      >
        <Play className="h-3.5 w-3.5 sm:h-4 sm:w-4 fill-current" />
        <span>{trailerOpen ? t("إخفاء التريلر", "Hide trailer", "Masquer la bande-annonce") : t("مشاهدة التريلر", "Watch trailer", "Voir la bande-annonce")}</span>
      </button>
      <button
        type="button"
        onClick={() => toggleFavorite(movie.slug, movie.type)}
        aria-pressed={fav}
        className={`${baseBtn} ${
          fav
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-card text-foreground border-border hover:bg-accent"
        }`}
      >
        <Heart className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${fav ? "fill-current" : ""}`} />
        <span className="hidden sm:inline">{t(fav ? "في المفضلة" : "المفضلة", fav ? "Favorited" : "Favorite", fav ? "Dans les favoris" : "Favori")}</span>
      </button>
      <button
        type="button"
        onClick={() => toggleWatchlist(movie.slug, movie.type)}
        aria-pressed={saved}
        aria-label={t(saved ? "محفوظ" : "احفظ", saved ? "Saved" : "Save", saved ? "Enregistré" : "Enregistrer")}
        className={`${baseBtn} ${
          saved
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-card text-foreground border-border hover:bg-accent"
        }`}
      >
        <Bookmark className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${saved ? "fill-current" : ""}`} />
        <span className="hidden sm:inline">{t(saved ? "محفوظ" : "احفظ", saved ? "Saved" : "Save", saved ? "Enregistré" : "Enregistrer")}</span>
      </button>
      <button
        type="button"
        onClick={share}
        aria-label={t("شارك", "Share", "Partager")}
        className={`${baseBtn} bg-card text-foreground border-border hover:bg-accent`}
      >
        {shared ? (
          <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
        ) : (
          <Share2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        )}
        <span className="hidden sm:inline">{shared ? t("تم النسخ", "Copied", "Copié") : t("شارك", "Share", "Partager")}</span>

      </button>
    </div>
  );
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1) + "K";
  return String(n);
}

function BadgeBar({
  kind, slug, threadId, isNew, isTrending, lang,
}: {
  kind: "movie" | "series";
  slug: string;
  threadId: string;
  isNew: boolean;
  isTrending: boolean;
  lang: LanguageCode;
}) {
  const views = useViews(slug);
  const comments = useCommentsCount(threadId);
  const U = UI_T[lang];
  // Unified pill style — every chip is the exact same capsule for parallel alignment.
  const pill =
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap h-7 px-3 rounded-full text-[11px] font-bold border backdrop-blur shadow-lg";
  return (
    <div className="flex flex-row flex-wrap gap-2 items-center">
      <span className={`${pill} bg-primary text-primary-foreground border-primary/60`}>
        <Film className="h-3.5 w-3.5 shrink-0" />
        {kind === "movie" ? U.movie : U.series}
      </span>
      {isTrending && (
        <span className={`${pill} bg-[var(--rating)]/15 text-[var(--rating)] border-[var(--rating)]/50`} title={U.trending}>
          <Flame className="h-3.5 w-3.5 shrink-0" />
          {U.trending}
        </span>
      )}
      {isNew && (
        <span className={`${pill} bg-emerald-500/15 text-emerald-300 border-emerald-400/50`} title={U.newBadge}>
          <Sparkles className="h-3.5 w-3.5 shrink-0" />
          {U.newBadge}
        </span>
      )}
      <span className={`${pill} bg-black/70 text-white border-white/20`} title={U.views}>
        <Eye className="h-3.5 w-3.5 shrink-0" /> {formatCount(views)}
      </span>
      <span className={`${pill} bg-black/70 text-white border-white/20`} title={U.comments}>
        <MessageSquare className="h-3.5 w-3.5 shrink-0" /> {formatCount(comments)}
      </span>
    </div>
  );
}
